import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, MessageCircle, Globe, Loader2 } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { WHATSAPP_URL, IMAGES } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      setSent(true);
      toast.success("Demo request received — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-line focus:border-vermilion outline-none py-4 text-bone placeholder:text-ash/60 text-base transition-colors duration-300 rounded-none";

  return (
    <main data-testid="contact-page" className="pt-40 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pb-32 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <Reveal y={20}>
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion">Contact</p>
          </Reveal>
          <h1 className="mt-8 font-display text-5xl md:text-7xl tracking-tighter leading-[0.95] text-bone">
            <MaskedLine delay={0.15}>Let's talk about</MaskedLine>
            <MaskedLine delay={0.3}>
              your <span className="italic text-vermilion">lifecycle.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-10 max-w-md text-ash text-base md:text-lg leading-relaxed">
              Thirty minutes. Your use case. Seven capabilities working as one connected layer — live, not slideware.
            </p>
            <div className="mt-12 space-y-6">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="group flex items-center gap-4 border border-line p-6 hover:border-vermilion transition-colors duration-300 max-w-md"
              >
                <MessageCircle size={22} strokeWidth={1} className="text-vermilion" />
                <div>
                  <p className="text-bone text-sm">Chat on WhatsApp</p>
                  <p className="text-ash text-xs mt-1 font-mono-tech uppercase tracking-[0.15em]">Fastest response</p>
                </div>
                <ArrowUpRight size={16} className="ml-auto text-ash group-hover:text-vermilion group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
              </a>
              <div className="flex items-center gap-4 p-6 border border-line max-w-md">
                <Globe size={22} strokeWidth={1} className="text-vermilion" />
                <div>
                  <p className="text-bone text-sm">Global — online &amp; on-site</p>
                  <p className="text-ash text-xs mt-1 font-mono-tech uppercase tracking-[0.15em]">Wherever your customers are</p>
                </div>
              </div>
              <div className="p-6 border border-line max-w-md">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-vermilion mb-3">Email</p>
                <p className="text-ash text-xs mb-2">Rahul Bhatia</p>
                <a href="mailto:sales@aiinaa.com" data-testid="contact-email-sales" className="block text-bone text-sm hover:text-vermilion transition-colors duration-300">sales@aiinaa.com</a>
                <a href="mailto:ceo@aiinaa.com" data-testid="contact-email-ceo" className="block mt-2 text-bone text-sm hover:text-vermilion transition-colors duration-300">ceo@aiinaa.com</a>
              </div>
              <div className="border border-line overflow-hidden max-w-md relative group">
                <img
                  src={IMAGES.sphere}
                  alt="Abstract sphere"
                  className="w-full h-52 md:h-64 object-cover opacity-70 transition-all duration-700 group-hover:opacity-90 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void/70 to-transparent" />
              </div>
            </div>
          </Reveal>
        </div>

        <div>
          <Reveal delay={0.3}>
            {sent ? (
              <div data-testid="contact-success-message" className="border border-line p-10 md:p-14 bg-surface/40 h-full flex flex-col justify-center">
                <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion">Request received</p>
                <h2 className="mt-6 font-display text-4xl md:text-5xl text-bone leading-tight">
                  Thank you, <span className="italic">{form.name.split(" ")[0]}.</span>
                </h2>
                <p className="mt-6 text-ash text-base leading-relaxed max-w-sm">
                  Your demo request is in. We'll reach out at {form.email} shortly — or tap WhatsApp for an immediate conversation.
                </p>
              </div>
            ) : (
              <form data-testid="demo-request-form" onSubmit={submit} className="border border-line p-10 md:p-14 bg-surface/40">
                <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion">Request a demo</p>
                <div className="mt-10 space-y-8">
                  <div>
                    <label htmlFor="contact-name" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-ash">Name</label>
                    <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Cooper" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-ash">Work email</label>
                    <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-ash">Company</label>
                    <input id="contact-company" data-testid="contact-company-input" required value={form.company} onChange={set("company")} placeholder="Company Inc." className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-ash">What are you trying to solve?</label>
                    <textarea id="contact-message" data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Churn is eating our membership base…" className={`${inputCls} resize-none`} />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={sending}
                    className="group w-full flex items-center justify-center gap-3 bg-vermilion text-white font-mono-tech text-xs uppercase tracking-[0.2em] px-8 py-5 transition-colors duration-300 hover:bg-vermilion-dark active:scale-[0.98] disabled:opacity-60"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />}
                    {sending ? "Sending…" : "Send request"}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </main>
  );
}
